import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ShareVisibilitySettings from '@components/plan/ShareVisibilitySettings';
import {
	DEFAULT_SHARE_VISIBILITY,
	normalizeShareVisibility,
	type ShareVisibility
} from '@utils/shareVisibility';
import {
	getCompanyInviteForPlan,
	updateCompanyInviteVisibilityForPlan
} from '@services/planInvites';

type CopyFn = (en: string, de: string, hr: string) => string;

export type ClientVisibilityModalProps = {
	open: boolean;
	onClose: () => void;
	planDatabaseId: number;
	companyUserId: number;
	copy: CopyFn;
	initialVisibility?: ShareVisibility | null;
	onSaved?: (visibility: ShareVisibility) => void;
};

export default function ClientVisibilityModal({
	open,
	onClose,
	planDatabaseId,
	companyUserId,
	copy,
	initialVisibility = null,
	onSaved
}: ClientVisibilityModalProps) {
	const [visibility, setVisibility] = useState<ShareVisibility>(DEFAULT_SHARE_VISIBILITY);
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [linkedClientLabel, setLinkedClientLabel] = useState<string | null>(null);
	const [inviteStatus, setInviteStatus] = useState<string | null>(null);

	useEffect(() => {
		if (!open) return;
		document.body.classList.add('modal-open');
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.classList.remove('modal-open');
			document.body.style.overflow = '';
		};
	}, [open]);

	useEffect(() => {
		if (!open) return;
		setError(null);
		setVisibility(normalizeShareVisibility(initialVisibility));

		let cancelled = false;
		const load = async () => {
			setLoading(true);
			try {
				const status = await getCompanyInviteForPlan(planDatabaseId, companyUserId);
				if (cancelled) return;
				if (!status.has_invite) {
					setError(
						copy(
							'No active invite for this plan. Invite a client first.',
							'Keine aktive Einladung für diesen Plan. Laden Sie zuerst einen Kunden ein.',
							'Nema aktivne pozivnice za ovaj plan. Prvo pozovite klijenta.'
						)
					);
					return;
				}
				setInviteStatus(status.status ?? null);
				setVisibility(normalizeShareVisibility(status.share_visibility));
				const client =
					status.linked_user_name || status.linked_user_email || status.invited_email || null;
				setLinkedClientLabel(client);
			} catch (err) {
				if (!cancelled) {
					setError(
						err instanceof Error
							? err.message
							: copy('Could not load visibility settings.', 'Sichtbarkeit konnte nicht geladen werden.', 'Postavke vidljivosti nisu učitane.')
					);
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		void load();
		return () => {
			cancelled = true;
		};
	}, [open, planDatabaseId, companyUserId, initialVisibility, copy]);

	if (!open) return null;

	const handleSave = async () => {
		setSaving(true);
		setError(null);
		try {
			const res = await updateCompanyInviteVisibilityForPlan(planDatabaseId, companyUserId, visibility);
			const saved = normalizeShareVisibility(res.share_visibility);
			onSaved?.(saved);
			onClose();
		} catch (err) {
			setError(
				err instanceof Error
					? err.message
					: copy('Could not save visibility settings.', 'Sichtbarkeit konnte nicht gespeichert werden.', 'Postavke vidljivosti nisu spremljene.')
			);
		} finally {
			setSaving(false);
		}
	};

	return createPortal(
		<>
			<div className="modal fade show d-block" tabIndex={-1} role="dialog" aria-modal="true" style={{ zIndex: 1070 }}>
				<div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
					<div className="modal-content">
						<div className="modal-header">
							<h5 className="modal-title">
								<i className="bi bi-eye me-2" aria-hidden />
								{copy('Client view settings', 'Kundenansicht', 'Postavke prikaza za klijenta')}
							</h5>
							<button
								type="button"
								className="btn-close"
								onClick={() => !saving && onClose()}
								aria-label={copy('Close', 'Schließen', 'Zatvori')}
								disabled={saving}
							/>
						</div>
						<div className="modal-body">
							<p className="text-muted small mb-3">
								{copy(
									'Choose what your client sees when they open this plan. Changes apply immediately — even after they have accepted the invite.',
									'Wählen Sie, was Ihr Kunde beim Öffnen dieses Plans sieht. Änderungen gelten sofort — auch nach Annahme der Einladung.',
									'Odaberite što klijent vidi kad otvori ovaj plan. Promjene vrijede odmah — i nakon prihvaćanja pozivnice.'
								)}
							</p>
							{linkedClientLabel && (
								<div className="alert alert-light border py-2 small mb-3">
									<span className="fw-semibold">{copy('Client', 'Kunde', 'Klijent')}:</span> {linkedClientLabel}
									{inviteStatus === 'pending' && (
										<span className="text-muted ms-2">
											({copy('invite pending', 'Einladung ausstehend', 'pozivnica na čekanju')})
										</span>
									)}
								</div>
							)}
							{loading ? (
								<div className="text-center py-4">
									<div className="spinner-border text-success" role="status">
										<span className="visually-hidden">{copy('Loading...', 'Laden...', 'Učitavanje...')}</span>
									</div>
								</div>
							) : (
								<ShareVisibilitySettings
									value={visibility}
									onChange={setVisibility}
									copy={copy}
									disabled={saving || Boolean(error)}
								/>
							)}
							{error && (
								<div className="alert alert-warning py-2 small mb-0 mt-2" role="alert">
									{error}
								</div>
							)}
						</div>
						<div className="modal-footer">
							<button
								type="button"
								className="btn btn-outline-secondary"
								onClick={() => onClose()}
								disabled={saving}
							>
								{copy('Cancel', 'Abbrechen', 'Odustani')}
							</button>
							<button
								type="button"
								className="btn btn-success"
								onClick={() => void handleSave()}
								disabled={saving || loading || Boolean(error)}
							>
								{saving ? (
									<>
										<span className="spinner-border spinner-border-sm me-2" aria-hidden />
										{copy('Saving…', 'Speichern…', 'Spremanje…')}
									</>
								) : (
									copy('Save client view', 'Kundenansicht speichern', 'Spremi prikaz za klijenta')
								)}
							</button>
						</div>
					</div>
				</div>
			</div>
			<div className="modal-backdrop fade show" style={{ zIndex: 1065 }} />
		</>,
		document.body
	);
}
